import { useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { PixelIcon } from './PixelIcon'
import { useNavigation } from '@/contexts/NavigationProvider'
import { useProgress } from '@/contexts/ProgressProvider'
import { computeDailyStatus } from '@/game/daily'
import { useGameAudio } from '@/contexts/GameAudioProvider'
import type { ScreenId } from '@/types/game'
import type { PixelIconName } from '@/game/pixelIcons'

interface NavEntry {
  screen: ScreenId
  label: string
  icon: PixelIconName
}

const ENTRIES: NavEntry[] = [
  { screen: 'home', label: 'Home', icon: 'home' },
  { screen: 'map', label: 'Play', icon: 'levelSelect' },
  { screen: 'endless', label: 'Endless', icon: 'infinity' },
  { screen: 'shop', label: 'Store', icon: 'shop' },
  { screen: 'profile', label: 'Account', icon: 'user' },
]

const MORE_ENTRIES: NavEntry[] = [
  { screen: 'characters', label: 'Characters', icon: 'mask' },
  { screen: 'daily', label: 'Daily Gift', icon: 'gift' },
  { screen: 'leaderboard', label: 'Grace', icon: 'trophy' },
  { screen: 'settings', label: 'Settings', icon: 'settings' },
  { screen: 'help', label: 'How to Play', icon: 'book' },
  { screen: 'credits', label: 'Credits', icon: 'book' },
]

const MORE_SCREENS = MORE_ENTRIES.map((e) => e.screen)

export function BottomNav() {
  const { screen, go } = useNavigation()
  const { save } = useProgress()
  const audio = useGameAudio()
  const [moreOpen, setMoreOpen] = useState(false)
  const dailyReady = computeDailyStatus(save.meta.daily, Date.now()).canClaim
  const moreActive = MORE_SCREENS.includes(screen)

  const goTo = (target: ScreenId) => {
    setMoreOpen(false)
    if (target === screen) return
    audio.play('click')
    go(target)
  }

  return (
    <>
      <AnimatePresence>
        {moreOpen && (
          <motion.div
            className="sm:hidden fixed inset-0"
            style={{ zIndex: 'var(--z-overlay)' as unknown as number }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <button
              aria-label="Close menu"
              onClick={() => setMoreOpen(false)}
              className="absolute inset-0 bg-black/55 backdrop-blur-sm"
            />
            <motion.div
              role="menu"
              aria-label="More"
              initial={{ y: '110%' }}
              animate={{ y: 0 }}
              exit={{ y: '110%' }}
              transition={{ type: 'spring', stiffness: 420, damping: 38 }}
              className="absolute inset-x-0 bottom-0 rounded-t-[var(--r-xl)] border-t border-[var(--edge)]/50 bg-[var(--surface)] px-3 pt-2 pb-[calc(0.75rem+var(--safe-bottom))]"
              style={{ boxShadow: '0 -18px 40px rgba(0,0,0,0.45)' }}
            >
              <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-[var(--edge)]" />
              <div className="grid grid-cols-2 gap-2">
                {MORE_ENTRIES.map((e) => {
                  const active = screen === e.screen
                  return (
                    <motion.button
                      key={e.screen}
                      role="menuitem"
                      whileTap={{ scale: 0.96 }}
                      onClick={() => goTo(e.screen)}
                      className="relative flex items-center gap-3 rounded-[var(--r-md)] bg-[var(--surface-raised)] px-3.5 py-3 text-left shadow-emboss"
                      style={{ color: active ? 'var(--ember)' : 'var(--text)' }}
                    >
                      <span
                        className="grid h-9 w-9 shrink-0 place-items-center rounded-[var(--r-sm)]"
                        style={{ background: 'var(--surface-sunken)', color: active ? 'var(--ember)' : 'var(--text-dim)' }}
                      >
                        <PixelIcon name={e.icon} size={18} />
                      </span>
                      <span className="text-sm font-bold">{e.label}</span>
                      {e.screen === 'daily' && dailyReady && (
                        <span className="ml-auto h-2 w-2 rounded-full bg-[var(--ember)] shadow-glow" />
                      )}
                    </motion.button>
                  )
                })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <nav
        aria-label="Main"
        className="sm:hidden shrink-0 border-t border-[var(--edge)]/50 bg-[var(--surface)] px-2 pt-1.5 pb-[calc(0.5rem+var(--safe-bottom))]"
        style={{
          zIndex: 'var(--z-nav)' as unknown as number,
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 -10px 24px rgba(0,0,0,0.35)',
        }}
      >
        <div className="mx-auto flex w-full max-w-md items-stretch">
          {ENTRIES.map((e) => {
            const active = screen === e.screen
            return (
              <motion.button
                key={e.screen}
                whileTap={{ scale: 0.92 }}
                aria-label={e.label}
                aria-current={active ? 'page' : undefined}
                onClick={() => goTo(e.screen)}
                className="relative flex flex-1 flex-col items-center gap-0.5 rounded-[var(--r-md)] py-1.5"
                style={{ color: active ? 'var(--ember)' : 'var(--text-faint)' }}
              >
                {active && (
                  <motion.span
                    layoutId="bottomnav-glow"
                    transition={{ type: 'spring', stiffness: 420, damping: 32 }}
                    className="absolute inset-x-2 inset-y-0 rounded-[var(--r-md)] bg-[var(--surface-raised)] shadow-emboss"
                    style={{ boxShadow: 'var(--shadow-emboss), 0 0 18px rgba(255,180,84,0.12)' }}
                  />
                )}
                <span className="relative">
                  <PixelIcon name={e.icon} size={21} />
                </span>
                <span className="relative text-[10px] font-bold tracking-wide">{e.label}</span>
              </motion.button>
            )
          })}
          <motion.button
            whileTap={{ scale: 0.92 }}
            aria-label="More"
            aria-haspopup="menu"
            aria-expanded={moreOpen}
            aria-current={moreActive && !moreOpen ? 'page' : undefined}
            onClick={() => {
              audio.play('click')
              setMoreOpen((v) => !v)
            }}
            className="relative flex flex-1 flex-col items-center gap-0.5 rounded-[var(--r-md)] py-1.5"
            style={{ color: moreOpen || moreActive ? 'var(--ember)' : 'var(--text-faint)' }}
          >
            {moreActive && !moreOpen && (
              <motion.span
                layoutId="bottomnav-glow"
                transition={{ type: 'spring', stiffness: 420, damping: 32 }}
                className="absolute inset-x-2 inset-y-0 rounded-[var(--r-md)] bg-[var(--surface-raised)] shadow-emboss"
                style={{ boxShadow: 'var(--shadow-emboss), 0 0 18px rgba(255,180,84,0.12)' }}
              />
            )}
            <span className="relative">
              <PixelIcon name="grid" size={21} />
              {dailyReady && (
                <motion.span
                  className="absolute -top-1 -right-1.5 h-2 w-2 rounded-full bg-[var(--ember)]"
                  animate={{ scale: [1, 1.35, 1], opacity: [1, 0.7, 1] }}
                  transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                />
              )}
            </span>
            <span className="relative text-[10px] font-bold tracking-wide">More</span>
          </motion.button>
        </div>
      </nav>
    </>
  )
}
